import { CalendarDate } from '@internationalized/date';
import {
  BillComplianceRule,
  BillType,
  CurrencyCode,
  Money,
  PaymentMethod,
  PaymentTerm,
  enumBillType,
  enumCurrencyCode,
  enumPaymentMethod,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { billCreateAtom } from '../_graphql/mutationBillCreate';
import { billGetNextDocumentNumberAtom } from '../_graphql/mutationBillGetNextDocumentNumber';
import { billUpdateAtom } from '../_graphql/mutationBillUpdate';
import { billDetailQueryAtom } from '../_graphql/queryBillDetail';
import { BillFormState, BillLineItemFormState } from '../types';

import { calculateTotalPrice } from '~/modules/trade/utils/money';

const initialBillFormState: BillFormState = {
  id: undefined,
  type: enumBillType.InboundBill,
  documentNumber: undefined,
  documentDate: null,
  eligibleAt: null,
  accountId: null,
  facilityId: null,
  paymentTerm: null,
  preferredPaymentMethod: enumPaymentMethod.ACH,
  compliance: [],
  notes: null,
  scaleTicketId: null,
  currencyCode: enumCurrencyCode.USD,
  lineItems: [],
};

// 🔘🔘🔘 PLAIN ATOMS - Form State
export const $billForm = atom<BillFormState>(initialBillFormState);
export const $currentStep = atom<number>(1);
export const $isSubmitting = atom<boolean>(false);
export const $errors = atom<Record<string, string>>({});

// 🟠🟠🟠 ACTIONS - Form Updates
export const $updateBillForm = atom(
  null,
  (get, set, updates: Partial<BillFormState>) => {
    const currentForm = get($billForm);
    set($billForm, { ...currentForm, ...updates });
  },
);

export const $resetBillForm = atom(null, (get, set) => {
  set($billForm, initialBillFormState);
  set($currentStep, 1);
  set($errors, {});
});

export const $setCurrentStep = atom(null, (get, set, step: number) => {
  set($currentStep, step);
});

// 🟠🟠🟠 ACTIONS - Line Item Management
export const $addLineItem = atom(null, (get, set) => {
  const form = get($billForm);
  const maxSortOrder = form.lineItems.reduce(
    (max, item) => Math.max(max, item.sortOrder),
    -1,
  );
  const newItem: BillLineItemFormState = {
    id: `temp-${Date.now()}-${Math.random()}`,
    description: '',
    quantity: null,
    unit: null,
    unitPrice: null,
    totalPrice: null,
    itemId: null,
    groupName: null,
    sortOrder: maxSortOrder + 1,
    isNew: true,
  };

  set($billForm, {
    ...form,
    lineItems: [...form.lineItems, newItem],
  });
});

export const $updateLineItem = atom(
  null,
  (
    get,
    set,
    {
      index,
      updates,
    }: { index: number; updates: Partial<BillLineItemFormState> },
  ) => {
    const form = get($billForm);
    const updatedItems = [...form.lineItems];
    const currentItem = updatedItems[index];

    const updatedItem = { ...currentItem, ...updates };

    if (updates.quantity !== undefined || updates.unitPrice !== undefined) {
      updatedItem.totalPrice = calculateTotalPrice(
        updatedItem.unitPrice,
        updatedItem.quantity,
      );
    }

    updatedItems[index] = updatedItem;

    set($billForm, {
      ...form,
      lineItems: updatedItems,
    });
  },
);

export const $removeLineItem = atom(null, (get, set, index: number) => {
  const form = get($billForm);
  const updatedItems = form.lineItems.filter((_, i) => i !== index);

  set($billForm, {
    ...form,
    lineItems: updatedItems,
  });
});

// 🟠🟠🟠 ACTIONS - Validation
export const BILL_REQUIRED_FIELDS = [
  'type',
  'documentDate',
  'facilityId',
  'paymentTerm',
  'preferredPaymentMethod',
] as const;

export type BillRequiredField = (typeof BILL_REQUIRED_FIELDS)[number];

const fieldValidators: Record<
  BillRequiredField,
  (form: BillFormState) => boolean
> = {
  type: (form) => !form.type,
  documentDate: (form) => !form.documentDate,
  facilityId: (form) => !form.facilityId,
  paymentTerm: (form) => !form.paymentTerm,
  preferredPaymentMethod: (form) => !form.preferredPaymentMethod,
};

const fieldErrorMessages: Record<BillRequiredField, string> = {
  type: 'Bill type is required',
  documentDate: 'Document date is required',
  facilityId: 'Facility is required',
  paymentTerm: 'Payment term is required',
  preferredPaymentMethod: 'Payment method is required',
};

const getMissingRequiredFields = (form: BillFormState): BillRequiredField[] =>
  BILL_REQUIRED_FIELDS.filter((field) => fieldValidators[field](form));

export const $validateBillForm = atom(
  null,
  (get, set): { isValid: boolean; errors: Record<string, string> } => {
    const form = get($billForm);
    const missingFields = getMissingRequiredFields(form);

    const errors = missingFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: fieldErrorMessages[field],
      }),
      {} as Record<string, string>,
    );

    set($errors, errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  },
);

export const $billMissingRequiredFields = atom((get) => {
  const form = get($billForm);
  return getMissingRequiredFields(form);
});

export const $billIsMissingRequired = atom(
  (get) => get($billMissingRequiredFields).length > 0,
);

// 🟢🟢🟢 REMOTE DATA - Existing Bill
export const $existingBill = billDetailQueryAtom((get) => ({
  variables: {
    id: get($billForm).id ?? '',
  },
  skip: !get($billForm).id || get($billForm)?.id?.startsWith('temp-'),
}));

// 🟠🟠🟠 ACTION - Next Document Number
export const $nextBillDocumentNumber = billGetNextDocumentNumberAtom();

// 🟠🟠🟠 ACTION -> CREATE bill
export const $createBill = billCreateAtom();

// 🟠🟠🟠 ACTION -> UPDATE bill
export const $updateBillAction = billUpdateAtom();

// 🟠🟠🟠 ACTION -> Populate Form from Bill Record
export const $populateBillFormFromRecord = atom(
  null,
  (
    get,
    set,
    bill: {
      id: string;
      type: BillType;
      documentNumber: string;
      documentDate: CalendarDate | null;
      eligibleAt: CalendarDate | null;
      accountId: string | null;
      facilityId: string;
      paymentTerm: PaymentTerm | null;
      preferredPaymentMethod: PaymentMethod | null;
      compliance: BillComplianceRule[];
      notes: string | null;
      scaleTicketId: string | null;
      currencyCode: CurrencyCode;
      lineItems: {
        id: string;
        description: string;
        quantity: number | null;
        unit: string | null;
        unitPrice: Money | null;
        totalPrice: Money | null;
        itemId: string | null;
        groupName: string | null;
        sortOrder: number;
      }[];
    },
  ) => {
    set($billForm, {
      id: bill.id,
      type: bill.type,
      documentNumber: bill.documentNumber,
      documentDate: bill.documentDate,
      eligibleAt: bill.eligibleAt,
      accountId: bill.accountId,
      facilityId: bill.facilityId,
      paymentTerm: bill.paymentTerm,
      preferredPaymentMethod: bill.preferredPaymentMethod,
      compliance: bill.compliance,
      notes: bill.notes,
      scaleTicketId: bill.scaleTicketId,
      currencyCode: bill.currencyCode,
      lineItems: bill.lineItems.map((li) => ({
        id: li.id,
        description: li.description,
        quantity: li.quantity,
        unit: li.unit,
        unitPrice: li.unitPrice,
        totalPrice: li.totalPrice,
        itemId: li.itemId,
        groupName: li.groupName,
        sortOrder: li.sortOrder,
        isNew: false,
      })),
    });
  },
);
